import { Link } from "react-router-dom";
import {
  HandHeart,
  HousePlus,
  Stethoscope,
  ClipboardPlus,
  Users,
  HeartHandshake,
  Check,
  ArrowRight,
  MapPin,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, FadeIn, Overline, GoldRule } from "@/components/Reveal";

const services = [
  {
    icon: HandHeart,
    title: "Personal Care Assistance",
    tagline: "Dignity in every daily routine",
    text: "Gentle, respectful support with the activities of daily living — so your loved one can feel confident, comfortable, and independent at home.",
    includes: ["Bathing, grooming & dressing", "Mobility & transfer assistance", "Toileting & incontinence care", "Medication reminders", "Meal preparation & feeding support"],
  },
  {
    icon: HousePlus,
    title: "Home Health Aide Services",
    tagline: "Certified support for everyday living",
    text: "Our certified home health aides keep households running smoothly while providing attentive, nurse-supervised personal support.",
    includes: ["Light housekeeping & laundry", "Grocery shopping & errands", "Escort to appointments", "Vital signs monitoring", "Care plan documentation"],
  },
  {
    icon: Stethoscope,
    title: "Skilled Nursing",
    tagline: "Clinical excellence at home",
    text: "Registered and licensed practical nurses deliver physician-ordered medical care in the comfort and safety of home.",
    includes: ["Wound care & dressing changes", "Medication administration & injections", "Chronic disease monitoring", "Post-surgical recovery care", "Coordination with physicians"],
  },
  {
    icon: ClipboardPlus,
    title: "Private Duty Nursing",
    tagline: "One-on-one care for complex needs",
    text: "Extended, continuous skilled nursing for clients with complex medical conditions who need dedicated, individualized attention.",
    includes: ["Hourly & extended-shift nursing", "Ventilator & tracheostomy care", "Tube feeding management", "Complex medication regimens", "Family training & education"],
  },
  {
    icon: Users,
    title: "Companion & Support Services",
    tagline: "Because connection is care",
    text: "Warm, engaging companionship that eases loneliness and keeps minds and spirits active.",
    includes: ["Conversation & social engagement", "Games, hobbies & activities", "Transportation & outings", "Reading & correspondence help", "Safety supervision"],
  },
  {
    icon: HeartHandshake,
    title: "Family & Caregiver Support",
    tagline: "Support for the whole family",
    text: "Caring for a loved one is a family journey. We give families the guidance, relief, and confidence they deserve.",
    includes: ["Respite care for family caregivers", "Care coordination & planning", "Family education & resources", "Regular progress updates", "24/7 on-call support"],
  },
];

const matrix = [
  { feature: "Companionship & social engagement", personal: true, skilled: false, companion: true },
  { feature: "Bathing, grooming & dressing", personal: true, skilled: false, companion: false },
  { feature: "Meal preparation & light housekeeping", personal: true, skilled: false, companion: true },
  { feature: "Medication reminders", personal: true, skilled: true, companion: false },
  { feature: "Medication administration & injections", personal: false, skilled: true, companion: false },
  { feature: "Wound care & clinical monitoring", personal: false, skilled: true, companion: false },
  { feature: "RN supervision of care plan", personal: true, skilled: true, companion: true },
];

const counties = [
  "Bergen", "Essex", "Hudson", "Middlesex", "Monmouth", "Morris",
  "Ocean", "Passaic", "Somerset", "Union",
];

const Services = () => (
  <main>
    <section data-testid="services-hero" className="relative overflow-hidden bg-blush-50 pb-16 pt-40 sm:pb-20">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full border border-gold/25" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full border border-gold/20" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <FadeIn>
          <Overline>Our Services</Overline>
        </FadeIn>
        <FadeIn delay={0.15}>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            Comprehensive home care, <em className="text-gold-deep">tailored to you</em>
          </h1>
        </FadeIn>
        <FadeIn delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal-muted sm:text-lg">
            Six signature services, one promise: dependable, personalized care
            delivered with warmth — throughout New Jersey.
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl space-y-8 px-5 sm:px-8">
        {services.map((s, i) => (
          <Reveal key={s.title} delay={0.05}>
            <div
              data-testid="service-detail-card"
              className={`group grid gap-8 rounded-[2rem] border border-blush-200/80 bg-white p-8 shadow-[0_2px_24px_rgba(31,41,55,0.04)] transition-[box-shadow,border-color] duration-300 hover:border-gold/40 hover:shadow-[0_18px_48px_rgba(232,180,188,0.22)] sm:p-10 lg:grid-cols-[auto_1fr_1.1fr] ${
                i % 2 === 1 ? "bg-blush-50/60" : ""
              }`}
            >
              <div className="flex items-start gap-6 lg:flex-col lg:gap-8">
                <span className="font-serif text-4xl font-light italic text-gold/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft">
                  <s.icon className="h-7 w-7 text-gold" strokeWidth={1.3} />
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-deep">
                  {s.tagline}
                </p>
                <h2 className="mt-2 font-serif text-2xl text-charcoal sm:text-3xl">{s.title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-charcoal-muted sm:text-base">
                  {s.text}
                </p>
              </div>
              <Accordion type="single" collapsible className="self-start rounded-2xl border border-blush-200 bg-white px-5">
                <AccordionItem value="includes" className="border-none">
                  <AccordionTrigger
                    data-testid={`service-includes-toggle-${i}`}
                    className="text-sm font-semibold uppercase tracking-[0.14em] text-charcoal hover:text-gold-deep hover:no-underline"
                  >
                    What's included
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2.5 pb-2">
                      {s.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-charcoal-muted">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section data-testid="comparison-section" className="bg-blush-50 py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="text-center">
          <Overline centered>Find the Right Fit</Overline>
          <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
            Compare levels of care
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-charcoal-muted sm:text-base">
            Not sure which service fits? This quick guide can help — and our
            care team will confirm the right level during your free consultation.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 overflow-x-auto">
          <table data-testid="comparison-table" className="w-full min-w-[560px] overflow-hidden rounded-2xl border border-blush-200 bg-white text-left text-sm shadow-[0_2px_20px_rgba(31,41,55,0.04)]">
            <thead>
              <tr className="border-b border-gold/25 bg-blush-100/60">
                <th className="px-6 py-4 font-serif text-base font-medium text-charcoal">Care feature</th>
                <th className="px-4 py-4 text-center font-serif text-base font-medium text-charcoal">Personal Care</th>
                <th className="px-4 py-4 text-center font-serif text-base font-medium text-charcoal">Skilled Nursing</th>
                <th className="px-4 py-4 text-center font-serif text-base font-medium text-charcoal">Companion Care</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.feature} className="border-b border-blush-100 last:border-none">
                  <td className="px-6 py-3.5 text-charcoal-light">{row.feature}</td>
                  {[row.personal, row.skilled, row.companion].map((v, j) => (
                    <td key={j} className="px-4 py-3.5 text-center">
                      {v ? (
                        <Check className="mx-auto h-4.5 w-4.5 h-5 w-5 text-gold" strokeWidth={2} />
                      ) : (
                        <span className="text-blush-300">&mdash;</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </div>
    </section>

    <section data-testid="coverage-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
        <Reveal>
          <Overline centered>Where We Serve</Overline>
          <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
            Proudly serving New Jersey families
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-wrap justify-center gap-3">
          {counties.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white px-5 py-2 text-sm text-charcoal-light transition-colors hover:bg-blush-50"
            >
              <MapPin className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
              {c} County
            </span>
          ))}
        </Reveal>
        <Reveal delay={0.25} className="mt-14">
          <GoldRule className="justify-center" />
          <p className="mx-auto mt-6 max-w-lg text-sm text-charcoal-muted">
            Every care journey begins with a conversation. Tell us about your
            loved one, and we'll design a plan together.
          </p>
          <Link to="/request-care" data-testid="services-cta-button" className="btn-pink mt-8">
            Request a Care Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Services;
